/**
 * Created by Andrey Popov on 21.10.2026
 */

const Map2dSpotlight = cc.Node.extend({
    ctor: function (mapView) {
        this._super();

        this.mapView = mapView;
        this.tinted = new Set();

        const cell = cleverapps.styles.Map2dView.cell;

        const shadow = this.shadow = new cc.Node();
        shadow.setCascadeOpacityEnabled(true);
        shadow.setOpacity(0);
        shadow.setScaleY(cell.height / cell.width);
        shadow.setPosition(mapView.alignInGrid(0, 0));
        shadow.setVisible(false);
        this.addChild(shadow);

        const scale = cell.width / Math.SQRT2;
        const pieces = this.pieces = new cc.Node();
        pieces.setCascadeOpacityEnabled(true);
        pieces.setCascadeColorEnabled(true);
        pieces.setColor(cleverapps.styles.COLORS.BLACK);
        pieces.setRotation(-45);
        pieces.setScale(scale, -scale);
        shadow.addChild(pieces);
    },

    show: function (cells, options = {}) {
        if (options.area) {
            const regions = Object.values(this.mapView.map2d.regions || {}).filter((region) => region.positions);
            const touched = regions.filter((region) => region.positions.some((position) => cells.some((cell) => cell.x === position.x && cell.y === position.y)));
            cells = cells.concat(...touched.map((region) => region.positions));
        }
        this.lit = new Set(cells.map(Unit.GetPositionKey));

        const layers = cleverapps.unique(Map2dSpotlight.LAYERS.map((layer) => this.mapView.layersViews[layer]));
        const views = layers.flatMap((layerView) => layerView.children.flatMap((cellView) => cellView.children)).concat(this.mapView.cloudsAbove.children);
        const dimmed = new Set();
        views.filter((view) => this.isDimmed(view)).forEach((view) => Map2dSpotlight.addNodes(view, dimmed));
        this.setTinted(dimmed);

        const columns = this.listSlots(cells.map((cell) => cell.x));
        const rows = this.listSlots(cells.map((cell) => cell.y));
        this.pieces.removeAllChildren();
        rows.forEach((row) => columns.forEach((column) => {
            const lit = column.cells.flatMap((x) => row.cells.filter((y) => this.isLit(x, y)).map((y) => cc.p(x, y)));
            if (lit.length === 0) {
                this.addPiece(bundles.pixel.frames.pixel_png, column, row);
            } else if (lit.length === 1 && column.cells.length === 2 && row.cells.length === 2) {
                this.addPiece(bundles.unit_highlighter.frames.spotlight_corner, column, row, { x: lit[0].x === column.cells[1], y: lit[0].y === row.cells[1] });
            }
        }));

        this.shadow.stopAllActions();
        this.shadow.setVisible(true);
        this.shadow.runAction(new cc.FadeTo(Map2dSpotlight.FADE, Map2dSpotlight.OPACITY));
    },

    hide: function () {
        if (!this.lit) {
            return;
        }

        this.lit = undefined;
        this.setTinted(new Set());

        this.shadow.stopAllActions();
        this.shadow.runAction(new cc.Sequence(
            new cc.FadeOut(Map2dSpotlight.FADE),
            new cc.Hide()
        ));
    },

    onAddView: function (view) {
        if (this.lit && this.isDimmed(view)) {
            this.setTinted(Map2dSpotlight.addNodes(view, new Set(this.tinted)));
        }
    },

    isLit: function (x, y) {
        return this.lit.has(Unit.GetPositionKey({ x, y }));
    },

    isDimmed: function (view) {
        if (view instanceof UnitAdditionalView) {
            return !this.isLit(view.unitView.unit.x, view.unitView.unit.y);
        }
        if (view instanceof FogBlockView) {
            return !view.fogBlock.head || !this.isLit(view.fogBlock.head.x, view.fogBlock.head.y);
        }
        if (view.cell) {
            return Map2dSpotlight.LAYERS.includes(view.cell.layer) && !this.isLit(view.cell.x, view.cell.y);
        }
        return true;
    },

    listSlots: function (values) {
        const far = this.mapView.map2d.getWidth() + this.mapView.map2d.getHeight();
        if (!values.length) {
            return [{ from: -far, to: far, cells: [] }];
        }

        const min = Math.min(...values);
        const max = Math.max(...values);
        const spill = Map2dSpotlight.SPILL;

        const slots = [{ from: -far, to: min - 0.5 - spill, cells: [] }];
        for (let i = min; i <= max; i++) {
            slots.push({ from: i - 0.5 - spill, to: i - 0.5 + spill, cells: [i - 1, i] });
            slots.push({ from: i - 0.5 + spill, to: i + 0.5 - spill, cells: [i] });
        }
        slots.push({ from: max + 0.5 - spill, to: max + 0.5 + spill, cells: [max, max + 1] });
        slots.push({ from: max + 0.5 + spill, to: far, cells: [] });
        return slots;
    },

    addPiece: function (frame, column, row, flip = {}) {
        const sprite = new cc.Sprite(frame);
        sprite.setScale((flip.x ? -1 : 1) * (column.to - column.from) / sprite.width, (flip.y ? -1 : 1) * (row.to - row.from) / sprite.height);
        sprite.setPosition((column.from + column.to) / 2, (row.from + row.to) / 2);
        this.pieces.addChild(sprite);
    },

    setTinted: function (nodes) {
        this.tinted.forEach((node) => {
            if (!nodes.has(node)) {
                const original = node.spotlightOriginal;
                Map2dSpotlight.tintTo(node, original.color, () => {
                    node.spotlightOriginal = undefined;
                    node.setCascadeColorEnabled(original.cascade);
                });
            }
        });

        nodes.forEach((node) => {
            if (!this.tinted.has(node)) {
                node.spotlightOriginal = node.spotlightOriginal || { color: node.getColor(), cascade: node.isCascadeColorEnabled() };
                node.setCascadeColorEnabled(false);
                Map2dSpotlight.tintTo(node, Map2dSpotlight.DIM);

                if (node instanceof LazyAsset && !node.isLoaded()) {
                    node.whenAssetLoaded(() => this.tinted.has(node) && this.setTinted(Map2dSpotlight.addNodes(node, new Set(this.tinted))));
                }
            }
        });

        this.tinted = nodes;
    }
});

Map2dSpotlight.addNodes = function (node, nodes) {
    nodes.add(node);
    node.children.forEach((child) => Map2dSpotlight.addNodes(child, nodes));
    return nodes;
};

Map2dSpotlight.tintTo = function (node, color, onDone = () => {}) {
    node.stopAction(node.spotlightTint);

    if (node.isRunning()) {
        node.spotlightTint = node.runAction(new cc.Sequence(
            new cc.TintTo(Map2dSpotlight.FADE, color.r, color.g, color.b),
            new cc.CallFunc(onDone)
        ));
    } else {
        node.setColor(color);
        onDone();
    }
};

Map2dSpotlight.LAYERS = [Map2d.LAYER_UNITS, Map2d.LAYER_FOG];
Map2dSpotlight.ZORDER = Map2d.ABOVE_GROUND + 0.5;
Map2dSpotlight.OPACITY = 185;
Map2dSpotlight.DIM = new cc.Color(80, 80, 80, 255);
Map2dSpotlight.FADE = 0.3;
Map2dSpotlight.SPILL = 0.125;
